import { makeAutoObservable, runInAction } from "mobx";

import { fetchCsspSuppliers } from "./csspService";
import type { CsspSupplier } from "./types";

export type CsspSuppliersStatus = "idle" | "loading" | "success" | "empty" | "error";

type FetchSuppliers = (cityId: string, signal?: AbortSignal) => Promise<CsspSupplier[]>;

/**
 * A feature-scoped, current-context cache. It deliberately has no persistent
 * backing or TTL: availability changes when city or session changes.
 */
export class CsspSuppliersStore {
  cityId: string | null = null;
  sessionKey = "guest";
  suppliers: CsspSupplier[] = [];
  status: CsspSuppliersStatus = "idle";
  error: Error | null = null;

  private generation = 0;
  private request: Promise<readonly CsspSupplier[]> | null = null;
  private controller: AbortController | null = null;

  constructor(private readonly fetchSuppliers: FetchSuppliers = fetchCsspSuppliers) {
    makeAutoObservable<this, "fetchSuppliers" | "request" | "controller" | "generation">(this, {
      fetchSuppliers: false,
      request: false,
      controller: false,
      generation: false,
    }, { autoBind: true });
  }

  setContext(cityId: string | null, sessionKey: string) {
    const normalizedCityId = cityId?.trim() || null;
    if (this.cityId === normalizedCityId && this.sessionKey === sessionKey) return;

    this.abortAndReset();
    this.cityId = normalizedCityId;
    this.sessionKey = sessionKey;
  }

  async ensureSuppliers(cityId = this.cityId): Promise<readonly CsspSupplier[]> {
    const normalizedCityId = cityId?.trim() || null;
    if (!normalizedCityId || normalizedCityId !== this.cityId) {
      throw new Error("CSSP supplier city is unresolved");
    }
    if (this.status === "success" || this.status === "empty") return [...this.suppliers];
    if (this.status === "loading" && this.request) return this.request;

    const generation = ++this.generation;
    const controller = new AbortController();
    this.controller = controller;
    this.status = "loading";
    this.error = null;

    const request = this.fetchSuppliers(normalizedCityId, controller.signal)
      .then((suppliers) => {
        if (!this.isCurrent(generation, normalizedCityId, controller)) return [...suppliers];
        runInAction(() => {
          this.suppliers = [...suppliers];
          this.status = suppliers.length ? "success" : "empty";
          this.error = null;
        });
        return [...suppliers];
      })
      .catch((error: unknown) => {
        if (!this.isCurrent(generation, normalizedCityId, controller)) throw error;
        const normalizedError = error instanceof Error ? error : new Error("Unable to load CSSP suppliers");
        runInAction(() => {
          this.suppliers = [];
          this.status = controller.signal.aborted ? "idle" : "error";
          this.error = controller.signal.aborted ? null : normalizedError;
        });
        throw normalizedError;
      })
      .finally(() => {
        if (!this.isCurrent(generation, normalizedCityId, controller)) return;
        runInAction(() => {
          this.request = null;
          this.controller = null;
        });
      });

    this.request = request;
    return request;
  }

  retry() {
    if (!this.cityId) return Promise.reject(new Error("CSSP supplier city is unresolved"));
    if (this.status === "loading") return this.request ?? Promise.resolve([...this.suppliers]);
    this.status = "idle";
    this.error = null;
    return this.ensureSuppliers(this.cityId);
  }

  reset() {
    this.abortAndReset();
  }

  dispose() {
    this.abortAndReset();
  }

  private isCurrent(generation: number, cityId: string, controller: AbortController) {
    return this.generation === generation && this.cityId === cityId && this.controller === controller;
  }

  private abortAndReset() {
    this.generation += 1;
    this.controller?.abort();
    this.controller = null;
    this.request = null;
    this.suppliers = [];
    this.status = "idle";
    this.error = null;
  }
}
